import type { ChainId } from '@repo/web3'
import { zipObject } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { type Address, erc20Abi, type ReadContractParameters } from 'viem'
import { useReadContracts } from 'wagmi'
type Props = {
  chainId: ChainId
  userAddress: Address | undefined
  spenderAddress: Address | undefined
  tokenAddresses: Address[]
}

type AllowanceContracts = ReadContractParameters<typeof erc20Abi, 'allowance'> & { chainId: number }

export function useTokenAllowances({
  chainId,
  userAddress,
  spenderAddress,
  tokenAddresses
}: Props) {
  const contracts =
    userAddress && spenderAddress
      ? tokenAddresses.map(
          tokenAddress =>
            ({
              chainId,
              address: tokenAddress,
              abi: erc20Abi,
              functionName: 'allowance',
              args: [userAddress, spenderAddress]
            }) satisfies AllowanceContracts
        )
      : []

  const { data, isLoading, isRefetching, refetch } = useReadContracts({
    contracts,
    allowFailure: false,
    query: {
      // `contracts.length > 0` restores wagmi v2's built-in empty-contracts
      // gating: v3 happily runs an empty read, caching `[]` under the shared
      // `['readContracts', { contracts: [] }]` key (chainId is dropped from
      // the key when no contract lacks one — vacuously true for `[]`), which
      // pollutes every other empty-contracts `useReadContracts` observer.
      enabled: !!spenderAddress && !!userAddress && contracts.length > 0,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false
    }
  })

  const allowancesByTokenAddress: Record<Address, bigint> = useMemo(
    () => (data ? zipObject(tokenAddresses, data) : {}),
    [data, tokenAddresses]
  )

  const allowanceFor = useCallback(
    (tokenAddress: Address): bigint => {
      return allowancesByTokenAddress[tokenAddress] ?? 0n
    },
    [allowancesByTokenAddress]
  )

  return {
    isAllowancesLoading: isLoading,
    isAllowancesRefetching: isRefetching,
    allowances: allowancesByTokenAddress,
    spenderAddress,
    refetchAllowances: refetch as unknown as () => Promise<void>,
    allowanceFor
  }
}
